/**
 * Formate un slug pays de route en libellé affichable (ex. `royaume-uni` → `Royaume-Uni`).
 * @param {string} countrySlug Segment route (ex. `france`, `royaume-uni`).
 * @returns {string} Libellé avec majuscule initiale par segment.
 */
function formatCountryLabelFromSlug(countrySlug: string): string {
  return countrySlug
    .split('-')
    .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('-')
}

/**
 * Ajoute le libellé pays au champ SEO de base.
 * @param {string | undefined} baseText Texte SEO renvoyé par l'API.
 * @param {string | undefined} countrySlug 2ᵉ segment de slug route (ex. `france`).
 * @returns {string | undefined} Texte enrichi, ou `undefined` si `baseText` est absent.
 */
function appendCountrySlugToSeoMetaField(
  baseText: string | undefined,
  countrySlug: string | undefined,
): string | undefined {
  if (!baseText) return undefined
  if (!countrySlug) return baseText
  return `${baseText} - ${formatCountryLabelFromSlug(countrySlug)}`
}

/**
 * Meta title : ajoute le pays dérivé du slug (pages diplômes / types d'écoles par pays).
 * @param {string | undefined} baseTitle `seodatas.title`.
 * @param {string | undefined} countrySlug 2ᵉ segment de slug route.
 * @returns {string | undefined} Titre enrichi.
 */
export function appendSeoMetaTitleWithCountrySlug(
  baseTitle: string | undefined,
  countrySlug: string | undefined,
): string | undefined {
  return appendCountrySlugToSeoMetaField(baseTitle, countrySlug)
}

/**
 * Meta description : ajoute le pays dérivé du slug (même règle que le title).
 * @param {string | undefined} baseDescription `seodatas.description`.
 * @param {string | undefined} countrySlug 2ᵉ segment de slug route.
 * @returns {string | undefined} Description enrichie.
 */
export function appendSeoMetaDescriptionWithCountrySlug(
  baseDescription: string | undefined,
  countrySlug: string | undefined,
): string | undefined {
  return appendCountrySlugToSeoMetaField(baseDescription, countrySlug)
}
